import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Button, Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import { uploadHandler } from "Utils";
import { DEFAULT_MODULE_TYPE, VIDEO_TYPES } from "Constant/constants";

const mimeType = 'video/webm; codecs="opus,vp8"';

export const VideoRecorder = ({ show, toggle, handleSave }) => {
  const { t } = useTranslation();

  const mediaRecorder = useRef(null);
  const liveVideoFeed = useRef(null);

  const [loading, setLoading] = useState(false);
  const [permission, setPermission] = useState(false);
  const [stream, setStream] = useState(null);
  const [recordedVideo, setRecordedVideo] = useState(null);
  const [videoBuffer, setVideoBuffer] = useState(null);
  const [time, setTime] = useState(0);
  const [recordingStatus, setRecordingStatus] = useState("inactive");
  const [videoChunks, setVideoChunks] = useState([]);

  // Permission
  const getCameraPermission = async () => {
    setRecordedVideo(null);
    //get video and audio permissions and then stream the result media stream to the videoSrc variable
    if ("MediaRecorder" in window) {
      try {
        const videoConstraints = {
          audio: false,
          video: true,
        };
        const audioConstraints = { audio: true };

        // create audio and video streams separately
        const audioStream = await navigator.mediaDevices.getUserMedia(
          audioConstraints
        );
        const videoStream = await navigator.mediaDevices.getUserMedia(
          videoConstraints
        );

        setPermission(true);

        //combine both audio and video streams

        const combinedStream = new MediaStream([
          ...videoStream.getVideoTracks(),
          ...audioStream.getAudioTracks(),
        ]);

        setStream(combinedStream);

        //set videostream to live feed player
        liveVideoFeed.current.srcObject = videoStream;
      } catch (err) {
        toast.error(err.message);
      }
    } else {
      toast.error("The MediaRecorder API is not supported in your browser.");
    }
  };

  useEffect(() => {
    getCameraPermission();
  }, []);

  const removeCameraPermission = () => {
    // Stop all tracks in the video and audio streams
    if (stream) {
      const tracks = stream.getTracks();
      tracks.forEach((track) => track.stop());
    }

    // Clear the video feed
    if (liveVideoFeed.current) {
      liveVideoFeed.current.srcObject = null;
    }

    setRecordingStatus("inactive");
    setStream(null);
    setPermission(false);
    setRecordedVideo(null);
  };

  // Handle
  const startRecording = async () => {
    setRecordingStatus("recording");

    const media = new MediaRecorder(stream, { mimeType });

    mediaRecorder.current = media;

    mediaRecorder.current.start();

    let localVideoChunks = [];

    mediaRecorder.current.ondataavailable = (event) => {
      if (typeof event.data === "undefined") return;
      if (event.data.size === 0) return;
      localVideoChunks.push(event.data);
    };

    setVideoChunks(localVideoChunks);
  };

  const stopRecording = async () => {
    setPermission(false);
    setRecordingStatus("inactive");
    mediaRecorder.current.stop();

    mediaRecorder.current.onstop = async () => {
      // const videoBlob = new Blob(videoChunks, { type: mimeType });
      // const videoUrl = URL.createObjectURL(videoBlob);
      // setRecordedVideo(videoUrl);

      const videoFile = new File(videoChunks, "recorded-video.mp4", {
        type: mimeType,
      });
      const mockEvent = {
        target: {
          files: [videoFile],
        },
      };
      const blobUrl = URL.createObjectURL(videoFile);

      // To Fix - Avoid unnecessary AWS Upload
      // const response = await uploadHandler(
      //   mockEvent,
      //   DEFAULT_MODULE_TYPE.chat,
      //   VIDEO_TYPES
      // );

      // Stop all tracks in the video and audio streams
      if (stream) {
        const tracks = stream.getTracks();
        tracks.forEach((track) => track.stop());
      }

      // Clear the video feed
      if (liveVideoFeed.current) {
        liveVideoFeed.current.srcObject = null;
      }

      // To Fix - Avoid unnecessary AWS Upload
      // if (response && response.url) {
      //   setStream(null);
      //   setRecordedVideo(response.url);
      //   setVideoChunks([]);
      // }

      setStream(null);
      setVideoBuffer(mockEvent);
      setRecordedVideo(blobUrl);
      setVideoChunks([]);
    };
  };

  const onUploadVideo = async (e) => {
    removeCameraPermission();
    setVideoBuffer(e);

    const file = e.target.files[0];
    if (file) {
      // To Fix - Avoid unnecessary AWS Upload
      // const response = await uploadHandler(
      //   e,
      //   DEFAULT_MODULE_TYPE.chat,
      //   VIDEO_TYPES
      // );
      // if (response && response.url) {
      //   setRecordedVideo(response.url);
      // }

      const blobUrl = URL.createObjectURL(file);
      setRecordedVideo(blobUrl);
    }
  };

  useEffect(() => {
    let interval;

    if (recordingStatus === "recording") {
      interval = setInterval(() => {
        setTime((prevTime) => prevTime + 1);
      }, 1000);
    } else {
      clearInterval(interval);
    }

    // Clean up on unmount
    return () => clearInterval(interval);
  }, [recordingStatus]);

  const formatTime = (timeInSeconds) => {
    const hours = String(Math.floor(timeInSeconds / 3600)).padStart(2, "0");
    const minutes = String(Math.floor((timeInSeconds % 3600) / 60)).padStart(
      2,
      "0"
    );
    const seconds = String(timeInSeconds % 60).padStart(2, "0");
    return `${hours}:${minutes}:${seconds}`;
  };

  // Submit & Reset
  const onSave = async (e) => {
    setLoading(true);
    // handleSave(recordedVideo);
    // toggle();
    if (videoBuffer) {
      const response = await uploadHandler(
        videoBuffer,
        DEFAULT_MODULE_TYPE.chat,
        VIDEO_TYPES
      );
      if (response && response.url) {
        handleSave(response.url);
      }
      toggle();
      setLoading(false);
    } else {
      setLoading(false);
    }
  };

  const onClose = () => {
    removeCameraPermission();
    toggle();
  };

  return (
    <Modal
      size="lg"
      show={show}
      onHide={onClose}
      centered
      backdrop="static"
      keyboard={false}
    >
      <Modal.Header closeButton>
        <Modal.Title className="d-flex align-items-center gap-3 w-100">
          Video Recorder
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="">
        <div className="video-recorder-container">
          <div className="d-flex flex-column row-gap-2 align-items-center position-relative">
            <div className="video-player w-100 rounded-3 overflow-hidden">
              {!recordedVideo ? (
                <video
                  ref={liveVideoFeed}
                  autoPlay
                  className="live-player"
                ></video>
              ) : null}
              {recordedVideo ? (
                <div className="recorded-player d-flex flex-column row-gap-3 w-100">
                  <video
                    className="recorded"
                    src={recordedVideo}
                    controls
                  ></video>
                  {/* <a
                    download
                    href={recordedVideo}
                    className="px-3 py-1 border border-grey-200 bg-grey-50 text-decoration-none text-grey-500 font-medium"
                  >
                    <i className="bx bx-download"></i> Download Recording
                  </a> */}
                </div>
              ) : null}
            </div>

            {/* <div className="video-controls">
                {!permission ? (
                  <button onClick={getCameraPermission} type="button">
                    Get Camera
                  </button>
                ) : null}
                {permission && recordingStatus === "inactive" ? (
                  <button onClick={startRecording} type="button" className="px-3 py-1 rounded-2 border border-grey-200 bg-grey-50">
                    <i className="bx bx-play"></i> Start Recording
                  </button>
                ) : null}
                {recordingStatus === "recording" ? (
                  <button onClick={stopRecording} type="button" className="px-3 py-1 rounded-2 border border-grey-200 bg-grey-50">
                    <i className="bx bx-stop"></i> Stop Recording
                  </button>
                ) : null}
              </div> */}
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <div className="d-flex justify-content-end align-items-center gap-2">
          <div className="upload-btn-wrapper rounded-2 cursor-pointer overflow-hidden">
            <button className="px-3 py-2 border border-grey-200 bg-grey-50 text-decoration-none rounded-lg text-sm font-medium text-grey-800">
              <i className="bx bx-upload"></i> Upload video
              <input type="file" onChange={onUploadVideo} />
            </button>
          </div>
          {recordedVideo && (
            <a
              download
              href={recordedVideo}
              className="px-3 py-2 text-grey-800 border border-grey-200 bg-grey-50 text-decoration-none rounded font-medium"
            >
              <i className="bx bx-download"></i> Download Recording
            </a>
          )}
          <div className="video-controls ms-auto d-flex gap-2">
            {recordingStatus === "recording" && (
              <span className="px-3 py-2 text-decoration-none rounded font-medium border border-grey-200 bg-grey-50">
                {formatTime(time)}
              </span>
            )}
            {!permission ? (
              <button
                onClick={getCameraPermission}
                type="button"
                className="px-3 py-2 border border-grey-200 bg-grey-50 text-decoration-none rounded-lg text-sm font-medium text-grey-800"
              >
                Get Camera
              </button>
            ) : null}
            {permission && recordingStatus === "inactive" ? (
              <button
                onClick={startRecording}
                type="button"
                className="px-3 py-2 text-decoration-none rounded-md font-medium text-white border border-red-600 bg-red-500"
              >
                <i className="bx bx-play"></i> Start Recording
              </button>
            ) : null}
            {recordingStatus === "recording" ? (
              <button
                onClick={stopRecording}
                type="button"
                className="px-3 py-2 text-decoration-none rounded-md font-medium text-white border border-red-600 bg-red-500"
              >
                <i className="bx bx-stop"></i> Stop Recording
              </button>
            ) : null}
          </div>
        
          {/* <Button onClick={onClose} className="px-3 py-2 text-grey-700 border border-grey-200 bg-grey-50 text-decoration-none font-medium rounded-md">
            {t("common.button.cancel")}
          </Button> */}
          <Button
            variant="success"
            onClick={onSave}
            className="px-3 py-2 text-decoration-none font-medium rounded-lg d-inline-flex align-items-center gap-1"
            disabled={loading || recordedVideo === null}
          >
            <i className="bx bx-save bx-xs me-1"></i>
            {t("common.button.save")}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};
